import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { removeLoadingKey } from '../../helpers/common'
import { toast } from 'react-toastify'

export const ForecastLoadingKeys = {
    getForecasts: "GET_ALL_FORECASTS",
    getForecast: "GET_FORECAST",
    getForecastValues: "GET_FORECAST_VALUES",
    getForecastOptions: "GET_FORECAST_OPTIONS",
    getDemandPlanningOptions: "GET_DEMAND_PLANNING_OPTIONS",
    createForecast: "CREATE_FORECAST",
    deleteForecast: "DELETE_FORECAST",
    updateForecast: "UPDATE_FORECAST"
}

const initialState = {
    loading: false,
    forecasts: [],
    selectedForecast: null,
    loadingKeys: [],
    forecastValues: [],
    forecastOptions: [],
    demandPlanningOptions: [],
    firstLoadComplete: false,
    error: null,
}

export const getForecastOptions = createAsyncThunk(
    'forecasts/getForecastOptions',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/forecast-options');
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const getDemandPlanningOptions = createAsyncThunk(
    'forecasts/getDemandPlanningOptions',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/forecast-demand-planning-options');
            return data;
        } catch (error) {
            if (typeof error.response?.data === 'string') {
                return rejectWithValue(error.response.data)
            }
            return rejectWithValue(error.response?.data?.errors || error.response?.data?.error)
        }
    }
)

export const getForecastValues = createAsyncThunk(
    'forecasts/getForecastValues',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/forecast-values');
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const getForecasts = createAsyncThunk(
    'forecasts/getForecasts',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/forecast');
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const getForecastRequest = createAsyncThunk(
    'forecasts/getForecastRequest',
    async ({ id }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/forecast-item', {
                params: {
                    id
                }
            });
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const createForecastRequest = createAsyncThunk(
    'forecasts/createForecastRequest',
    async ({
        forecastId,
        name,
        period
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/forecast/create', { forecastId, name, period });
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const deleteForecastRequest = createAsyncThunk(
    'forecasts/deleteForecastRequest',
    async ({
        id
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.delete(`/api/v1/forecast/delete/${id}`);
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const upsertForecastDataRequest = createAsyncThunk(
    'forecasts/upsertForecastDataRequest',
    async ({
        id,
        editedData
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/forecast/upsert', { id, editedData });
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const forecastSlice = createSlice({
    name: 'forecasts',
    initialState,
    reducers: {
        clearSelectedForecast: (state) => {
            state.selectedForecast = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getForecastValues.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, ForecastLoadingKeys.getForecastValues];
            })
            .addCase(getForecastValues.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.forecastValues = action.payload.items;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(ForecastLoadingKeys.getForecastValues, state.loadingKeys);
            })
            .addCase(getForecastValues.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.forecastValues = [];
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(ForecastLoadingKeys.getForecastValues, state.loadingKeys);
            })
            .addCase(getForecastOptions.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, ForecastLoadingKeys.getForecastOptions];
            })
            .addCase(getForecastOptions.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.forecastOptions = action.payload.items;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(ForecastLoadingKeys.getForecastOptions, state.loadingKeys);
            })
            .addCase(getForecastOptions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.forecastOptions = [];
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(ForecastLoadingKeys.getForecastOptions, state.loadingKeys);
            })
            .addCase(getDemandPlanningOptions.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, ForecastLoadingKeys.getDemandPlanningOptions];
            })
            .addCase(getDemandPlanningOptions.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.demandPlanningOptions = action.payload.items;
                state.loadingKeys = removeLoadingKey(ForecastLoadingKeys.getDemandPlanningOptions, state.loadingKeys);
            })
            .addCase(getDemandPlanningOptions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.demandPlanningOptions = [];
                state.loadingKeys = removeLoadingKey(ForecastLoadingKeys.getDemandPlanningOptions, state.loadingKeys);
            })
            .addCase(getForecastRequest.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, ForecastLoadingKeys.getForecast];
            })
            .addCase(getForecastRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.selectedForecast = action.payload.item;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(ForecastLoadingKeys.getForecast, state.loadingKeys);
            })
            .addCase(getForecastRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.selectedForecast = null;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(ForecastLoadingKeys.getForecast, state.loadingKeys);
            })
            .addCase(getForecasts.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, ForecastLoadingKeys.getForecasts];
            })
            .addCase(getForecasts.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.forecasts = action.payload.items;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(ForecastLoadingKeys.getForecasts, state.loadingKeys);
            })
            .addCase(getForecasts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.forecasts = [];
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(ForecastLoadingKeys.getForecasts, state.loadingKeys);
            })
            .addCase(createForecastRequest.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, ForecastLoadingKeys.createForecast];
            })
            .addCase(createForecastRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.loadingKeys = removeLoadingKey(ForecastLoadingKeys.createForecast, state.loadingKeys);
                toast.success(`Forecast created successfully.`, { position: "bottom-right" })
            })
            .addCase(createForecastRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.loadingKeys = removeLoadingKey(ForecastLoadingKeys.createForecast, state.loadingKeys);
                toast.error(
                    `Failed to create forecast. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(deleteForecastRequest.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, ForecastLoadingKeys.deleteForecast];
            })
            .addCase(deleteForecastRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.loadingKeys = removeLoadingKey(ForecastLoadingKeys.deleteForecast, state.loadingKeys);
                toast.success(`Forecast deleted successfully.`, { position: "bottom-right" })
            })
            .addCase(deleteForecastRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.loadingKeys = removeLoadingKey(ForecastLoadingKeys.deleteForecast, state.loadingKeys);
                toast.error(
                    `Forecast can't be deleted. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(upsertForecastDataRequest.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, ForecastLoadingKeys.updateForecast];
            })
            .addCase(upsertForecastDataRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.loadingKeys = removeLoadingKey(ForecastLoadingKeys.updateForecast, state.loadingKeys);
                toast.success(`Forecast updated successfully.`, { position: "bottom-right" })
            })
            .addCase(upsertForecastDataRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.loadingKeys = removeLoadingKey(ForecastLoadingKeys.updateForecast, state.loadingKeys);
                toast.error(
                    `Forecast can't be updated. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
    }
})
export const { clearSelectedForecast } = forecastSlice.actions;
export default forecastSlice.reducer 